import { useState, useEffect } from "react";
import { IoIosClose, IoIosArrowBack } from "react-icons/io";
import { ProductModel } from "@/types";
import { useTranslation } from "react-i18next";
import Carousel from "./Carousel";
import { usePlayer } from "@/lib/PlayerContext";
import { useAnalytics } from "@/lib/AnalyticsContext";
import { tryEnterPiP, isIOS, tryExitPiP } from "@/lib/pip";
import { getOrCreateGaUserId } from "@/lib/gaUserId";

export interface ShopModalProps {
  id: string;
  open: boolean;
  onClose: () => void;
  fullScreen: boolean;
  playerWidth: number;
  landscape?: boolean;
  isMobile?: boolean;
}

const ProductImage = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className="tw-aspect-square tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-gray-100">
      {!isLoaded && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
          <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-gray-200 tw-border-t-gray-400 tw-rounded-full tw-animate-spin"></div>
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`tw-w-full tw-h-full tw-object-cover tw-transition-opacity tw-duration-300 ${
          isLoaded ? "tw-opacity-100" : "tw-opacity-0"
        }`}
        onLoad={() => setIsLoaded(true)}
      />
    </div>
  );
};

const ShopModal = ({
  id,
  open,
  onClose,
  fullScreen,
  playerWidth,
  landscape = false,
  isMobile = false,
}: ShopModalProps) => {
  const { t } = useTranslation();
  const { products, meta } = usePlayer();
  const analytics = useAnalytics();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductModel | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"gallery" | "iframe">("gallery");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  // デバイスの向きを監視
  useEffect(() => {
    const mediaQuery = window.matchMedia("(orientation: portrait)");
    const handleChange = (e: MediaQueryListEvent) => {
      setIsPortrait(e.matches);
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    tryExitPiP();
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (open) {
      setIsClosing(false);
      // モーダルのマウント直後にvisibilityを変更
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
      setIsLoading(true);
      // 商品画像のプリロード
      const preloadImages = async () => {
        const imagePromises = products.map((p) => {
          if (p.thumbnails?.[0]) {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = resolve;
              img.src = p.thumbnails?.[0] ?? "";
            });
          }
          return Promise.resolve();
        });
        await Promise.all(imagePromises);
        setIsLoading(false);
      };
      preloadImages();
    } else {
      setIsVisible(false);
      // アニメーション完了後にモーダルを非表示
      timeoutId = setTimeout(() => {
        setSelectedProduct(null);
        setViewMode("gallery");
      }, 300);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [open, products]);

  // 選択した商品に応じてビューモードを設定
  useEffect(() => {
    if (selectedProduct) {
      // 商品がiframeUrlを持っていれば、初期表示はiframeモード、そうでなければギャラリーモード
      if (
        selectedProduct.iframeUrl &&
        selectedProduct.iframeUrl.trim() !== ""
      ) {
        setViewMode("iframe");
      } else {
        setViewMode("gallery");
      }
    }
  }, [selectedProduct]);

  if (!open) return null;

  const handleBack = () => {
    setSelectedProduct(null);
    setViewMode("gallery");
  };

  const hasIframe =
    selectedProduct?.iframeUrl && selectedProduct.iframeUrl.trim() !== "";

  const renderProductList = () => (
    <div
      className={`tw-grid ${landscape ? "tw-grid-cols-3" : "tw-grid-cols-2"} tw-gap-2 tw-px-4`}
    >
      {isLoading
        ? // スケルトンローディング
          Array.from({ length: landscape ? 3 : 4 }).map((_, index) => (
            <div key={index} className="tw-space-y-2">
              <div className="tw-aspect-square tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
              <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-animate-pulse"></div>
              <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-1/2 tw-animate-pulse"></div>
            </div>
          ))
        : products.map((p) => (
            <div
              key={p.id}
              className="tw-cursor-pointer tw-space-y-2"
              onClick={() => {
                // iOS では商品選択時に PiP を開始（購入ボタンで window.open をブロックさせないため）
                if (isIOS()) {
                  tryEnterPiP(id);
                }
                setSelectedProduct(p);
                analytics.sendLiveCommerceEvent.viewProduct(p.id, meta.id);
              }}
            >
              <ProductImage src={p.thumbnails?.[0] ?? ""} alt={p.name} />
              <div className="tw-mt-1 tw-text-sm tw-font-medium tw-truncate">
                {p.name}
              </div>
              {p.price && !isNaN(Number(p.price)) && (
                <div className="tw-text-sm tw-text-green-600">
                  {String(
                    t("product.price", {
                      price: Number(p.price).toLocaleString(),
                    })
                  )}
                </div>
              )}
            </div>
          ))}
    </div>
  );

  const renderProductDetail = (product: ProductModel) => {
    const hasIframe = product.iframeUrl && product.iframeUrl.trim() !== "";

    if (hasIframe) {
      return (
        <div className="tw-h-full">
          <div className="tw-mb-4 tw-rounded-lg tw-overflow-hidden tw-bg-gray-100 tw-h-full">
            <iframe
              src={product.iframeUrl}
              className="tw-w-full tw-h-full tw-border-0"
              title={product.name}
              allowFullScreen
              loading="lazy"
              allow="payment; camera; microphone;"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                zoom: fullScreen ? 1 : 0.65,
              }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            ></iframe>
          </div>
        </div>
      );
    }

    return (
      <div className="tw-px-4">
        <div className="tw-mb-4">
          <Carousel images={product.thumbnails ?? []}></Carousel>
        </div>

        <div className="tw-font-bold tw-text-base tw-mb-2">{product.name}</div>
        <div className="tw-py-1 tw-break-words tw-whitespace-pre-wrap tw-leading-6 tw-text-gray-600 tw-text-sm">
          <div
            dangerouslySetInnerHTML={{
              __html: product.description,
            }}
          />
        </div>
        {product.price && !isNaN(Number(product.price)) && (
          <div className="tw-font-semibold tw-text-green-600 tw-text-sm tw-mt-2">
            {String(
              t("product.price", {
                price: Number(product.price).toLocaleString(),
              })
            )}
          </div>
        )}
        {product.url && (
          <div className="tw-text-right tw-mt-4">
            <button
              className="tw-bg-green-600 hover:tw-bg-green-500 tw-text-white tw-py-1.5 tw-px-4 tw-rounded-full tw-border-none tw-outline-none tw-shadow-md hover:tw-cursor-pointer tw-transition-colors tw-text-sm"
              onClick={(e) => {
                e.stopPropagation();
                // iOS 以外では購入ボタンで PiP を開始（iOS は商品選択時に既に開始済み）
                if (!isIOS()) {
                  tryEnterPiP(id);
                }
                // ecProductIdがある場合はクエリパラメータを付与
                let targetUrl = product.url;
                if (product.ecProductId && product.url) {
                  try {
                    const url = new URL(product.url);
                    url.searchParams.set('fv_episode_id', meta.id || '');
                    url.searchParams.set('fv_user_id', getOrCreateGaUserId());
                    url.searchParams.set('fv_product_id', product.id);
                    targetUrl = url.toString();
                  } catch (err) {
                    // URL解析に失敗した場合は元のURLを使用
                    console.warn('Failed to parse product URL:', err);
                  }
                }
                // window.open をユーザージェスチャー内で直接呼ぶ
                window.open(targetUrl, "_blank", "noopener,noreferrer");
              }}
            >
              {String(t("product.buy"))}
            </button>
          </div>
        )}
      </div>
    );
  };

  // 高さとmaxWidthを動的に計算
  const getModalHeight = () => {
    // 横長設定(16:9)の場合
    if (landscape) {
      if (isMobile) {
        if (isPortrait) {
          // スマホ縦向きの場合
          return hasIframe && viewMode === "iframe" ? "50svh" : "45svh";
        }
        // スマホ横向きの場合
        return hasIframe && viewMode === "iframe" ? "80svh" : "70svh";
      } else {
        // PC横向き（landscapeプレイヤー）の場合
        if (fullScreen) {
          return hasIframe && viewMode === "iframe" ? "400px" : "350px";
        }
        return hasIframe && viewMode === "iframe" ? "330px" : "240px";
      }
    } else {
      // 縦長設定(9:16)の場合
      if (fullScreen) {
        return hasIframe && viewMode === "iframe" ? "400px" : "350px";
      }
      return hasIframe && viewMode === "iframe" ? "330px" : "280px";
    }
  };

  const getModalMaxWidth = () => {
    if (landscape) {
      if (isMobile) {
        // スマホ横画面の場合は横幅を広く
        return "min(90vw, 500px)";
      }
      // PC横画面の場合
      return fullScreen ? `min(80vw, 600px)` : `${playerWidth}px`;
    }
    // 縦画面
    return fullScreen ? `calc((100vh - 48px) * 9/16)` : `${playerWidth}px`;
  };

  return (
    <>
      <div
        className={`tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-end tw-justify-center tw-transition-all tw-duration-300 tw-ease-out ${
          isVisible && !isClosing ? "tw-opacity-100" : "tw-opacity-0"
        }`}
        style={{
          backgroundColor: fullScreen ? "rgba(0, 0, 0, 0.5)" : "transparent",
          pointerEvents: isVisible && !isClosing ? "auto" : "none",
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div
          className="tw-bg-white/70 tw-backdrop-blur-sm tw-rounded-t-[20px] tw-transition-all tw-duration-300 tw-ease-out tw-w-full"
          style={{
            transform:
              isVisible && !isClosing ? "translateY(0)" : "translateY(100%)",
            height: getModalHeight(),
            margin: "0 8px",
            maxWidth: getModalMaxWidth(),
          }}
        >
          <div className="tw-font-sans tw-h-full tw-overflow-auto">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-border-b tw-border-gray-200">
              {selectedProduct ? (
                <button
                  className="tw-text-gray-800 tw-bg-transparent tw-border-none tw-outline-none tw-shadow-none hover:tw-cursor-pointer tw-flex tw-items-center"
                  onClick={handleBack}
                >
                  <IoIosArrowBack size="1.5em" />
                  <span className="tw-ml-1">{String(t("common.back"))}</span>
                </button>
              ) : (
                <div className="tw-font-medium tw-text-gray-800">
                  {String(t("product.title"))}
                </div>
              )}
              <button
                className="tw-text-gray-800 tw-bg-transparent tw-border-none tw-outline-none tw-shadow-none hover:tw-cursor-pointer"
                onClick={handleClose}
              >
                <IoIosClose size="3em" />
              </button>
            </div>
            {products.length === 0 ? (
              <div className="tw-text-center tw-py-4">
                {String(t("product.no_products"))}
              </div>
            ) : selectedProduct ? (
              renderProductDetail(selectedProduct)
            ) : (
              renderProductList()
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShopModal;
